import React, { useMemo, useState } from 'react';
import { Box, SelectChangeEvent } from '@mui/material';
import dayjs from 'dayjs';
import branchesMock from '../../data/mockedData/schedules/branches.json';
import centersMock from '../../data/mockedData/schedules/centers.json';
import type { ScheduleCenter } from '../../types/schedules';
import SchedulesBreadcrumbs from './SchedulesBreadcrumbs';
import SchedulesHeaderTitle from './Header/SchedulesHeaderTitle';
import SchedulesCenterSelect from './Header/SchedulesCenterSelect';
import SchedulesDateNavigation from './Header/SchedulesDateNavigation';
import SchedulesManageScheduleButton from './Header/SchedulesManageScheduleButton';
import SchedulesStatusSwitch from './Header/SchedulesStatusSwitch';

interface SchedulesHeaderProps {
  onStatusChange?: (enabled: boolean) => void;
  onManageScheduleClick?: () => void;
}

const SchedulesHeader = React.memo(function SchedulesHeader({
  onStatusChange,
  onManageScheduleClick,
}: SchedulesHeaderProps) {
  const centersOptions = useMemo(() => {
    return (centersMock as ScheduleCenter[]).map((center: ScheduleCenter) => ({
      value: center.id,
      label: center.name,
    }));
  }, []);

  const branchesOptions = useMemo(() => {
    return (branchesMock as ScheduleCenter[]).map((branch: ScheduleCenter) => ({
      value: branch.id,
      label: branch.name,
    }));
  }, []);

  const [branch, setBranch] = useState(branchesOptions[0]?.value || 'transol');
  const [center, setCenter] = useState(centersOptions[0]?.value || 'transol');
  const [statusEnabled, setStatusEnabled] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => dayjs().add(1, 'day'));

  const isCenterDisabled = centersOptions.length <= 1;
  const isBranchDisabled = branchesOptions.length <= 1;

  const handleBranchChange = (event: SelectChangeEvent) => {
    setBranch(event.target.value);
  };

  const handleCenterChange = (event: SelectChangeEvent) => {
    setCenter(event.target.value);
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setStatusEnabled(enabled);
    onStatusChange?.(enabled);
  };

  const handleDateChange = (date: dayjs.Dayjs) => {
    setSelectedDate(date);
  };

  const handleManageScheduleClick = () => {
    onManageScheduleClick?.();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
      }}
    >
      <SchedulesBreadcrumbs />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          width: '100%',
        }}
      >
        <SchedulesHeaderTitle />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            rowGap: '12px',
            columnGap: '16px',
          }}
        >
          <SchedulesCenterSelect
            value={branch}
            options={branchesOptions}
            onChange={handleBranchChange}
            disabled={isBranchDisabled}
            label='Filial'
            width='220px'
          />
          <SchedulesCenterSelect
            value={center}
            options={centersOptions}
            onChange={handleCenterChange}
            disabled={isCenterDisabled}
            label='Centro de Escalas'
            width='220px'
          />
          <SchedulesDateNavigation value={selectedDate} onChange={handleDateChange} />
          <SchedulesManageScheduleButton onClick={handleManageScheduleClick} />
          <SchedulesStatusSwitch checked={statusEnabled} onChange={handleStatusChange} />
        </Box>
      </Box>
      <Box sx={{ width: '100%', height: '12px' }} />
    </Box>
  );
});

export default SchedulesHeader;
